import { useEffect, useState } from "react";
import Header from "../../components/header/Header";
import { useTranslation } from "react-i18next";
import Input from "../../components/shared/ui/input/Input";
import useDebounce from "../../lib/Debounce";
import { useLocation, useNavigate } from "react-router";
import { TableOrder } from "../../components/table-orders/TableOrders";
import DashboardCard from "../../components/card/DashboardCard";
import {
    useOrders,
    TOrdersDashboard,
    TOrder,
} from "../../context/OrdersProvider";
import { AlertsWrapper } from "../../components/alert/AlertsWrapper";
import LoadingSpinner from "../../components/shared/ui/Spinner";
import FilterOrderPanel from "../../components/sidepanel/FilterOrderPanel";
import FilterButton from "../../components/shared/ui/Button/FilterButton";

export default function Orders() {
    const {
        limit,
        offset,
        ordersList,
        getOrderList,
        fromDate,
        toDate,
        selectedDevice,
        setLimit,
        setSelectedOrder,
        setOffset,
        getOrderDashboard,
        ordersLoading,
        alertMsgs,
        setFromDate,
        setToDate,
        setSelectedDevice
    } = useOrders();

    const location = useLocation();
    const navigate = useNavigate();
    const [searchValue, setSearchValue] = useState("");
    const [activeTab, setActiveTab] = useState("");
    const [dashboard, setDashboard] = useState({} as TOrdersDashboard);
    const [IsFilterPanelOpen, setIsFilterPanelOpen] = useState(false)
    const { t } = useTranslation();
    const i18nOrderDirectory = (key: string): string =>
        t(`orders-directory.${key}`);
    const debouncedSearch = useDebounce(searchValue, 500);

    const tabs = [
        { id: "", label: i18nOrderDirectory("all") },
        { id: "in_progress", label: i18nOrderDirectory("in-progress") },
        { id: "completed", label: i18nOrderDirectory("completed") },
        { id: "failed", label: i18nOrderDirectory("failed") },
    ];

    const handleRowClick = (order: TOrder) => {
        setSelectedOrder(order);
        navigate(`/orders/${order.id}`, {
            state: { id: order.id },
        });
    };

    const handleFilterClick = () => {
        setIsFilterPanelOpen(true);
    };

    const handleSearch = (value: string) => {
        setOffset(0);
        setSearchValue(value);
    };

    const handleFilter = (id: string) => {
        setActiveTab(id);
        setOffset(0);
    };

    const onClearFilter = () => {
        setFromDate("");
        setToDate("");
        setSelectedDevice(null);
        setOffset(0);
    }

    useEffect(() => {
        if (location?.state?.status) {
            setActiveTab(location?.state?.status);
        }
    }, [location?.state?.status]);

    useEffect(() => {
        const fetchDashboard = async () => {
            const response = await getOrderDashboard();
            if (response) {
                setDashboard(response);
            }
        };
        fetchDashboard();
    }, []);

    useEffect(() => {
        const searchParam = debouncedSearch || undefined;
        const statusParam = activeTab || undefined;
        getOrderList({
            search: searchParam,
            status: statusParam,
            date_after: fromDate,
            date_before: toDate,
            target_device: selectedDevice
        });
    }, [
        debouncedSearch,
        activeTab,
        limit,
        offset,
        selectedDevice,
        fromDate,
        toDate
    ]);

    return (
        <>
            <div className="relative h-screen flex flex-col overflow-hidden">
                <Header title={i18nOrderDirectory("orders")} />
                {alertMsgs && (
                    <div className="p-4">
                        <AlertsWrapper alertState={alertMsgs} />
                    </div>
                )}
                <div className={`
                            flex flex-col overflow-auto min-h-0 transition-all duration-500 ease-in-out
                            ${IsFilterPanelOpen ? 'w-[calc(100%-460px)]' : 'w-full'}
                        `}>
                    <div className="flex flex-col transition-all duration-500 ease-in-out overflow-auto">
                        <div className="flex-shrink-0 p-4 bg-white border-b">

                            <div className="flex justify-around mb-4">
                                <DashboardCard
                                    title={i18nOrderDirectory("all-orders")}
                                    count={dashboard?.all_orders_count}
                                    icon="all_inbox"
                                />
                                <DashboardCard
                                    title={i18nOrderDirectory("in-progress")}
                                    count={dashboard?.orders_in_progress}
                                    icon="arrow_outward"
                                />
                                <DashboardCard
                                    title={i18nOrderDirectory("completed")}
                                    count={dashboard?.orders_in_done}
                                    icon="done_all"
                                />
                                <DashboardCard
                                    title={i18nOrderDirectory("failed")}
                                    count={dashboard?.orders_rejected}
                                    icon="cancel"
                                />
                            </div>

                            <div className="flex justify-between items-center">
                                <div className="h-auto">
                                    {tabs.map((tab) => (
                                        <button
                                            key={tab.id}
                                            onClick={() => handleFilter(tab.id)}
                                            className={`flex-1 p-2 text-sm font-medium transition-colors ${activeTab === tab.id
                                                ? "text-primeblue border-b-2 border-primeblue"
                                                : "text-primary-light"
                                                }`}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex gap-2">
                                    <FilterButton
                                        icon="filter_lists"
                                        label={i18nOrderDirectory('filters')}
                                        onClick={handleFilterClick}
                                        active={IsFilterPanelOpen}
                                        clearable={!!(fromDate || toDate || selectedDevice)}
                                        onClear={onClearFilter}
                                    />
                                    <div>
                                        <Input
                                            onClear={() => setSearchValue("")}
                                            name="search"
                                            onChange={(e) => handleSearch(e.target.value)}
                                            value={searchValue}
                                            placeholder={i18nOrderDirectory("search")}
                                            size="lg"
                                            clearable
                                            rightIcon="search"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {ordersLoading ? (
                            <div className="h-screen flex items-center justify-center">
                                <LoadingSpinner size="lg" text="Loading orders..." />
                            </div>
                        ) : (
                            <div className="flex-1">
                                <TableOrder
                                    offset={offset}
                                    setOffset={setOffset}
                                    handleRowClick={handleRowClick}
                                    setLimit={setLimit}
                                    ordersList={ordersList}
                                    limit={limit}
                                    filterNotFound={false}
                                />
                            </div>
                        )}
                        <FilterOrderPanel
                            className={`
                                        absolute top-0 right-0 h-full w-[450px] bg-white shadow-lg
                                        transform transition-transform duration-500 ease-in-out z-10
                                        ${IsFilterPanelOpen ? 'translate-x-0' : 'translate-x-full'}
                                    `}
                            isOpen={IsFilterPanelOpen}
                            onClose={() => setIsFilterPanelOpen(false)}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
